import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { IonicModule, ToastController } from '@ionic/angular';
import { BerJuttatasApi } from './juttatas.api';

/**
 *  A JUTTATÁSOK ÉS AZ EFO PARAMÉTEREI – kis szerkesztő a képernyők „Paraméterek" ablakában.
 *
 *  A kulcsok és korlátok intézményenként és évenként szerkeszthetők (ber_juttatas_param). Minden
 *  sornál látszik, honnan jön az érték: az Évi paraméterekből (ott szerkeszthető, itt csak
 *  olvasható), az év saját beállításából, egy korábbi évből örökölve, vagy a program
 *  alapértékéből. A „visszaállítás" az év saját értékét törli. A %-os kulcsokat százalékban
 *  mutatjuk, törtként tároljuk (mint az Évi paramétereknél).
 */
@Component({
  selector: 'app-ber-juttatas-param',
  standalone: true,
  imports: [CommonModule, FormsModule, IonicModule],
  templateUrl: './juttatas-param.component.html',
  styleUrls: ['../../naplo/naplo-shared.scss', '../ber-shared.scss', './juttatas-param.component.scss'],
})
export class JuttatasParamComponent implements OnInit {
  @Input() ugyfelId: string | null = null;
  @Input() ev = new Date().getFullYear();
  /** Mely csoportok látszanak (kozos, telefon, ajandek, szep, efo). */
  @Input() csoportok: string[] = ['kozos'];
  @Output() mentve = new EventEmitter<void>();

  sorok: any[] = [];
  mentes = false;
  static readonly CSOPORT_CIM: Record<string, string> = {
    kozos: 'Közös kulcsok', telefon: 'Cégtelefon', ajandek: 'Ajándékok', szep: 'SZÉP-kártya', efo: 'Egyszerűsített foglalkoztatás',
  };
  csoportCim = JuttatasParamComponent.CSOPORT_CIM;

  constructor(private api: BerJuttatasApi, private toast: ToastController) {}

  async ngOnInit() { await this.betolt(); }

  async betolt() {
    if (!this.ugyfelId) return;
    try {
      const r: any = await this.api.get(`/param/${this.ugyfelId}?ev=${this.ev}`);
      this.sorok = (r.parameterek || []).filter((p: any) => this.csoportok.includes(p.csoport))
        .map((p: any) => ({ ...p, szerk: this.megjelenit(p, p.ertek), eredeti: this.megjelenit(p, p.ertek), torol: false }));
    } catch { this.hiba('A paraméterek nem tölthetők be.'); }
  }

  get csoportLista(): string[] { return this.csoportok.filter((c) => this.sorok.some((s) => s.csoport === c)); }
  csoportSorai(c: string) { return this.sorok.filter((s) => s.csoport === c); }

  private megjelenit(p: any, v: any): number { const n = Number(v) || 0; return p.egyseg === '%' ? Math.round(n * 1e6) / 1e4 : n; }
  private tarol(p: any, v: any): number { const n = Number(v) || 0; return p.egyseg === '%' ? n / 100 : n; }

  forrasCim(p: any): string {
    if (p.forras === 'beallitas') return 'Évi paraméterek';
    if (p.forras === 'ev') return `${this.ev}. évi beállítás`;
    if (String(p.forras).startsWith('orokolt:')) return `${String(p.forras).slice(8)}. évből`;
    return 'alapérték';
  }
  egyseg(p: any): string { return p.egyseg === 'x' ? '×' : p.egyseg; }
  alapSzoveg(p: any): string { return `${this.megjelenit(p, p.alap)} ${this.egyseg(p)}`; }
  visszaallit(p: any) { p.torol = true; p.szerk = this.megjelenit(p, p.alap); }

  async ment() {
    const ertekek: Record<string, number | null> = {};
    for (const p of this.sorok) {
      if (p.forras === 'beallitas') continue;
      if (p.torol && p.forras === 'ev') { ertekek[p.kulcs] = null; continue; }
      if (Number(p.szerk) !== Number(p.eredeti)) ertekek[p.kulcs] = this.tarol(p, p.szerk);
    }
    if (!Object.keys(ertekek).length) { this.ok('Nincs változás.'); return; }
    this.mentes = true;
    try {
      await this.api.put(`/param/${this.ugyfelId}?ev=${this.ev}`, { ertekek });
      this.ok('Paraméterek mentve.');
      await this.betolt();
      this.mentve.emit();
    } catch (e: any) { this.hiba(e?.error?.error || 'Mentési hiba.'); }
    finally { this.mentes = false; }
  }

  private async ok(m: string) { (await this.toast.create({ message: m, duration: 1600, color: 'success' })).present(); }
  private async hiba(m: string) { (await this.toast.create({ message: m, duration: 2600, color: 'danger' })).present(); }
}
